import {
  country_enum,
  crrem_type_of_use_enum,
  crrem_type_with_sfh_enum,
  eu_taxonomy_compliance_enum,
  eu_taxonomy_datasource_enum,
  type_of_use_enum,
} from '@predium/enums';
import { PartialRecord } from './targetPaths/TargetPathTypes';

//this is to make sure they are evaluated in the right order, even when the order in the enum should change
// Use Record<> to make sure that we get a compile error when adding new values to the enum
const EuTaxonomyComplianceOrderRecord: Record<eu_taxonomy_compliance_enum, null> = {
  [eu_taxonomy_compliance_enum.COMPLIANT]: null,
  [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: null,
  [eu_taxonomy_compliance_enum.NON_COMPLIANT]: null,
  [eu_taxonomy_compliance_enum.UNKNOWN]: null,
};

export const EuTaxonomyComplianceOrder: eu_taxonomy_compliance_enum[] = Object.keys(
  EuTaxonomyComplianceOrderRecord,
) as eu_taxonomy_compliance_enum[];

// first number: upper bound for primary energy to satisfy eu taxonomy compliance (top 15%)
// second number: upper bound for primary energy to satisfy DNSH critera (top 30%)
// All standard values are taken from https://index-esg.com/de/
type EUTaxonomyBenchmark = {
  [eu_taxonomy_compliance_enum.COMPLIANT]: number;
  [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: number;
};

export const EUTaxonomyComplianceBenchmarks: Record<
  country_enum,
  Record<crrem_type_with_sfh_enum, EUTaxonomyBenchmark>
> = {
  [country_enum.DE]: {
    [crrem_type_of_use_enum.MFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 95,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 120,
    },
    [type_of_use_enum.SFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 95,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 120,
    },
    [crrem_type_of_use_enum.OFFICE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 119,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 148,
    },
    [crrem_type_of_use_enum.RETAIL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 156,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 201,
    },
    [crrem_type_of_use_enum.SHOPPING_CENTER]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 156,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 201,
    },
    [crrem_type_of_use_enum.HOTEL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 193,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 255,
    },
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 193,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 255,
    },
    [crrem_type_of_use_enum.HEALTH_FACILITY]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 159,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 191,
    },
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 92,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 126,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 92,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 126,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 92,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 126,
    },
  },
  // No exact values for Austria, fallback to values from Europe
  [country_enum.AT]: {
    [crrem_type_of_use_enum.MFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 99,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 138,
    },
    [type_of_use_enum.SFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 99,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 138,
    },
    [crrem_type_of_use_enum.OFFICE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 132,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 172,
    },
    [crrem_type_of_use_enum.RETAIL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 147,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 200,
    },
    [crrem_type_of_use_enum.SHOPPING_CENTER]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 147,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 200,
    },
    [crrem_type_of_use_enum.HOTEL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 192,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 253,
    },
    // BLU-2024
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 192,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 253,
    },
    [crrem_type_of_use_enum.HEALTH_FACILITY]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 178,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 222,
    },
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 63,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 97,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 63,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 97,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 63,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 97,
    },
  },
  // No exact values for Poland (Shopping Center), fallback to values from Europe
  // The rest is based on the Predium academy data
  [country_enum.PL]: {
    [crrem_type_of_use_enum.MFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 83.18,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 101.04,
    },
    [type_of_use_enum.SFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 73.2,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 100.88,
    },
    [crrem_type_of_use_enum.OFFICE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 106.45,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 144.56,
    },
    [crrem_type_of_use_enum.RETAIL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 106.45,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 144.56,
    },
    [crrem_type_of_use_enum.SHOPPING_CENTER]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 106.45,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 144.56,
    },
    [crrem_type_of_use_enum.HOTEL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 106.45,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 144.56,
    },
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 80.18,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 110.71,
    },
    [crrem_type_of_use_enum.HEALTH_FACILITY]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 159.4,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 221.59,
    },
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 103.96,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 142.77,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 103.96,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 142.77,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 103.96,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 142.77,
    },
  },
  [country_enum.FR]: {
    [crrem_type_of_use_enum.MFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 142,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 188,
    },
    [type_of_use_enum.SFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 142,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 188,
    },
    [crrem_type_of_use_enum.OFFICE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 146,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 187,
    },
    [crrem_type_of_use_enum.RETAIL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 154,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 221,
    },
    [crrem_type_of_use_enum.SHOPPING_CENTER]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 154,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 221,
    },
    [crrem_type_of_use_enum.HOTEL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 216,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 286,
    },
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 216,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 286,
    },
    [crrem_type_of_use_enum.HEALTH_FACILITY]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 210,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 261,
    },
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 50,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 79,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 50,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 79,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 50,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 79,
    },
  },
  [country_enum.UK]: {
    [crrem_type_of_use_enum.OFFICE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 163,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 193,
    },
    [crrem_type_of_use_enum.RETAIL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 147,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 188,
    },
    [crrem_type_of_use_enum.SHOPPING_CENTER]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 147,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 188,
    },
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 63,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 93,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 63,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 93,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 63,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 93,
    },
    [crrem_type_of_use_enum.HOTEL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 231,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 294,
    },
    [crrem_type_of_use_enum.HEALTH_FACILITY]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 248,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 307,
    },
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 231,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 294,
    },
    [crrem_type_of_use_enum.MFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 193,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 233,
    },
    [type_of_use_enum.SFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 193,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 233,
    },
  },
  // TODO DATA-425 (DATA-481): placeholder Czechia EU Taxonomy thresholds — copied from DE
  // Source: 2024 CBA study with the Czech Ministry of Industry and Trade, based on the
  [country_enum.CZ]: {
    [crrem_type_of_use_enum.MFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 104,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 137,
    },
    [type_of_use_enum.SFH]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 137,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 200,
    },
    [crrem_type_of_use_enum.OFFICE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 152,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 208,
    },
    [crrem_type_of_use_enum.RETAIL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 214,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 301,
    },
    [crrem_type_of_use_enum.SHOPPING_CENTER]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 214,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 301,
    },
    [crrem_type_of_use_enum.HOTEL]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 185,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 257,
    },
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 159,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 242,
    },
    [crrem_type_of_use_enum.HEALTH_FACILITY]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 185,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 244,
    },
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 214,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 301,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 146,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 206,
    },
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: {
      [eu_taxonomy_compliance_enum.COMPLIANT]: 146,
      [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: 206,
    },
  },
};

/**
 * Which primary-energy figure a building is assessed against. Predium computes EU Taxonomy
 * compliance from two independent sources, and Drees & Sommer publishes a separate top-15%
 * bound for each:
 * - DEMAND_BASED: the modelled demand primary energy (persisted energy_path).
 * - CONSUMPTION_BASED: the primary energy derived from metered consumption.
 * DEFAULT_INDEX ignores this distinction (one table for both).
 */
export enum EuTaxonomyEnergyBasis {
  DEMAND_BASED = 'DEMAND_BASED',
  CONSUMPTION_BASED = 'CONSUMPTION_BASED',
}

export type EuTaxonomyComplianceOptions = {
  /** Organization's chosen data source. Defaults to DEFAULT_INDEX (the existing index-esg.com values). */
  datasource?: eu_taxonomy_datasource_enum;
  /** Which primary-energy figure is being classified. Defaults to DEMAND_BASED. */
  basis?: EuTaxonomyEnergyBasis;
};

// Drees & Sommer "Top 15%" primary-energy upper bounds (kWhPE/m²·a).
// Source: https://linear.app/predium/document/mapping-drees-and-sommer-top-15percent-to-predium-953699f72b54
//
// These tables only carry the COMPLIANT (top-15%) bound — Drees & Sommer has NO 30%
// (MEETS_DNSH_CRITERIA) tier. Missing data resolves per classifyEuTaxonomyCompliance's two rules:
// a country absent from the mapping uses Germany's table wholesale, while a type absent from a
// present country's table yields UNKNOWN (never a fallback to the DEFAULT_INDEX bounds).
// Only DE/FR/PL are covered by the mapping; other available countries (AT/CZ/UK) use Germany's values.
// The mapping doc also lists UK (in kgCO₂/m²·a — a carbon-intensity metric this primary-energy
// classifier can't compare against) and NL (not yet onboarded as a country_enum); both are
// intentionally not implemented here.
//
// Where the doc has no data for a type of use (HEALTH_FACILITY everywhere; HOTEL and
// LODGING_LEISURE_RECREATION in PL), it conservatively reuses the lowest commercial value
// excluding warehouses (the Office column).
const DreesUndSommerDemandBasedBenchmarks: PartialRecord<
  country_enum,
  PartialRecord<crrem_type_with_sfh_enum, number>
> = {
  [country_enum.DE]: {
    // Residential (MFH/SFH) demand-based compliance is a construction-year rule, not a primary-energy
    // bound — see DreesUndSommerDemandBasedResidentialMinConstructionYear below. They are intentionally
    // absent from this numeric table.
    [crrem_type_of_use_enum.OFFICE]: 145,
    [crrem_type_of_use_enum.RETAIL]: 146,
    [crrem_type_of_use_enum.SHOPPING_CENTER]: 146,
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: 228,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: 51,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: 51,
    [crrem_type_of_use_enum.HOTEL]: 195,
    [crrem_type_of_use_enum.HEALTH_FACILITY]: 145,
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: 195,
  },
  [country_enum.FR]: {
    [crrem_type_of_use_enum.OFFICE]: 70,
    [crrem_type_of_use_enum.RETAIL]: 90,
    [crrem_type_of_use_enum.SHOPPING_CENTER]: 90,
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: 90,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: 110,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: 110,
    [crrem_type_of_use_enum.HOTEL]: 140,
    [crrem_type_of_use_enum.HEALTH_FACILITY]: 70,
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: 140,
  },
  [country_enum.PL]: {
    [crrem_type_of_use_enum.OFFICE]: 155,
    [crrem_type_of_use_enum.RETAIL]: 160,
    [crrem_type_of_use_enum.SHOPPING_CENTER]: 160,
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: 160,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: 155,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: 155,
    [crrem_type_of_use_enum.HOTEL]: 155,
    [crrem_type_of_use_enum.HEALTH_FACILITY]: 155,
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: 155,
  },
};

const DreesUndSommerConsumptionBasedBenchmarks: PartialRecord<
  country_enum,
  PartialRecord<crrem_type_with_sfh_enum, number>
> = {
  [country_enum.DE]: {
    [crrem_type_of_use_enum.MFH]: 69,
    [type_of_use_enum.SFH]: 69,
    [crrem_type_of_use_enum.OFFICE]: 179,
    [crrem_type_of_use_enum.RETAIL]: 191,
    [crrem_type_of_use_enum.SHOPPING_CENTER]: 191,
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: 273,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: 96,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: 96,
    [crrem_type_of_use_enum.HOTEL]: 213,
    [crrem_type_of_use_enum.HEALTH_FACILITY]: 179,
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: 213,
  },
  [country_enum.FR]: {
    [crrem_type_of_use_enum.OFFICE]: 70,
    [crrem_type_of_use_enum.RETAIL]: 90,
    [crrem_type_of_use_enum.SHOPPING_CENTER]: 90,
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: 90,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: 110,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: 110,
    [crrem_type_of_use_enum.HOTEL]: 140,
    [crrem_type_of_use_enum.HEALTH_FACILITY]: 70,
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: 140,
  },
  [country_enum.PL]: {
    [crrem_type_of_use_enum.OFFICE]: 151,
    [crrem_type_of_use_enum.RETAIL]: 169,
    [crrem_type_of_use_enum.SHOPPING_CENTER]: 169,
    [crrem_type_of_use_enum.RETAIL_WAREHOUSE]: 169,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_COLD]: 151,
    [crrem_type_of_use_enum.DISTRIBUTION_WAREHOUSE_WARM]: 151,
    [crrem_type_of_use_enum.HOTEL]: 151,
    [crrem_type_of_use_enum.HEALTH_FACILITY]: 151,
    [crrem_type_of_use_enum.LODGING_LEISURE_RECREATION]: 151,
  },
};

// Drees & Sommer expresses the demand-based residential top-15% criterion as a construction-year
// rule rather than a primary-energy bound: a residential building is COMPLIANT when it was built in or
// after this year, and NON_COMPLIANT otherwise. (Consumption-based residential still uses a numeric bound.)
const DreesUndSommerDemandBasedResidentialMinConstructionYear: PartialRecord<country_enum, number> = {
  [country_enum.DE]: 2009,
};

const isResidentialBuildingType = (buildingType: crrem_type_with_sfh_enum): boolean =>
  buildingType === crrem_type_of_use_enum.MFH || buildingType === type_of_use_enum.SFH;

/**
 * Classifies a building's EU Taxonomy compliance from a primary-energy figure, honoring the
 * organization's chosen data source.
 *
 * Missing data resolves the same way for every data source:
 * 1. A country with no table at all for the selected data source uses GERMANY's table of that
 *    data source wholesale (including, for Drees & Sommer, the German residential rules).
 * 2. A country that has a table but no value for the building type yields UNKNOWN — never a
 *    fallback to another data source or to Germany.
 *
 * - `DEFAULT_INDEX` (default): the existing index-esg.com behavior — COMPLIANT / MEETS_DNSH_CRITERIA /
 *   NON_COMPLIANT — identical for both energy bases.
 * - `DREES_UND_SOMMER`: uses the Drees & Sommer top-15% bound for the given `basis`. It has no 30%
 *   tier, so a found bound classifies only as COMPLIANT or NON_COMPLIANT.
 *   Exception: demand-based residential (MFH/SFH) uses a construction-year rule (COMPLIANT iff built in
 *   or after the effective country's threshold year) that fully determines the result.
 *
 * The `yearConstructed >= 2021 -> UNKNOWN` rule applies only to DEFAULT_INDEX. Drees & Sommer
 * classifications are returned as-is, regardless of construction year.
 *
 * `buildingType` is the already-resolved lookup key (SFH keeps its own key; every other type is the
 * CRREM bucket) — callers do the `type_of_use_enum` → `crrem_type_with_sfh_enum` mapping.
 */
export function classifyEuTaxonomyCompliance(
  country: country_enum,
  buildingType: crrem_type_with_sfh_enum,
  primaryEnergy: number,
  yearConstructed: number,
  {
    datasource = eu_taxonomy_datasource_enum.DEFAULT_INDEX,
    basis = EuTaxonomyEnergyBasis.DEMAND_BASED,
  }: EuTaxonomyComplianceOptions = {},
): eu_taxonomy_compliance_enum {
  if (datasource === eu_taxonomy_datasource_enum.DREES_UND_SOMMER) {
    const dreesUndSommerBenchmarks =
      basis === EuTaxonomyEnergyBasis.CONSUMPTION_BASED
        ? DreesUndSommerConsumptionBasedBenchmarks
        : DreesUndSommerDemandBasedBenchmarks;
    // A country absent from the Drees & Sommer mapping uses Germany's table wholesale.
    const effectiveCountry = dreesUndSommerBenchmarks[country] ? country : country_enum.DE;

    // Demand-based residential compliance is a construction-year rule, not a primary-energy bound.
    if (basis === EuTaxonomyEnergyBasis.DEMAND_BASED && isResidentialBuildingType(buildingType)) {
      const minConstructionYear = DreesUndSommerDemandBasedResidentialMinConstructionYear[effectiveCountry];
      if (minConstructionYear !== undefined) {
        return yearConstructed >= minConstructionYear
          ? eu_taxonomy_compliance_enum.COMPLIANT
          : eu_taxonomy_compliance_enum.NON_COMPLIANT;
      }
      // No year rule for this country — fall through to the numeric lookup (absent -> UNKNOWN).
    }

    // A type missing from the (effective) country's table is unclassifiable — no fallback.
    const compliantUpperBound = dreesUndSommerBenchmarks[effectiveCountry]?.[buildingType];
    if (compliantUpperBound === undefined) {
      return eu_taxonomy_compliance_enum.UNKNOWN;
    }
    // Drees & Sommer never grants the 30% (MEETS_DNSH_CRITERIA) tier.
    return primaryEnergy <= compliantUpperBound
      ? eu_taxonomy_compliance_enum.COMPLIANT
      : eu_taxonomy_compliance_enum.NON_COMPLIANT;
  }

  // DEFAULT_INDEX: the existing index-esg.com behavior, with both compliance tiers. The table is
  // currently complete for every country_enum × type, so the two missing-data rules below are
  // unreachable today — they take effect as soon as a country is added with partial (or no) data.
  const defaultIndexBenchmarks: PartialRecord<
    country_enum,
    PartialRecord<crrem_type_with_sfh_enum, EUTaxonomyBenchmark>
  > = EUTaxonomyComplianceBenchmarks;
  const countryBenchmarks = defaultIndexBenchmarks[country] ?? defaultIndexBenchmarks[country_enum.DE];
  const benchmarks = countryBenchmarks?.[buildingType];
  if (benchmarks === undefined) {
    return eu_taxonomy_compliance_enum.UNKNOWN;
  }
  let compliance: eu_taxonomy_compliance_enum;
  if (primaryEnergy <= benchmarks[eu_taxonomy_compliance_enum.COMPLIANT]) {
    compliance = eu_taxonomy_compliance_enum.COMPLIANT;
  } else if (primaryEnergy <= benchmarks[eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]) {
    compliance = eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA;
  } else {
    compliance = eu_taxonomy_compliance_enum.NON_COMPLIANT;
  }

  // Buildings constructed from 2021 onwards can't be assessed with these benchmarks.
  if (compliance !== eu_taxonomy_compliance_enum.NON_COMPLIANT && yearConstructed >= 2021) {
    compliance = eu_taxonomy_compliance_enum.UNKNOWN;
  }
  return compliance;
}
